import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { tokens, panelStyles } from '../listPanelStyles';
import { createLabel, deleteLabel, putDesignCategories } from '../../utils/explorerApi';
import AudioPlayChip from '../AudioPlayChip';
import { categoryLabelValue, labelToIndex, OTHER_LABEL } from './categoryLabels';

// CategoryEditPanel — the unified "Edit categories" overlay for a classified
// free-text question. It does BOTH kinds of category editing in one place:
//   1. Rename existing categories (per-category display label; index-stable, no
//      re-classification), and re-run the automatic classifier.
//   2. Reassign individual responses (per-row) to any category / Other / a new
//      category. Reassignment writes an author-label OVERLAY that overrides the
//      classifier and is preserved across re-classification — the machine never
//      fights a manual call. Ambiguous responses show a blank category to fill.
//
// It subsumes the old Ambiguous review: ambiguous rows are simply the ones with
// status "Ambiguous" (filter to them). Dismiss → back to the graph.
//
// State (rows/labels/categories) is owned by the parent (ClassifiedTextCanvas)
// so the chart and this table stay in sync; writes here call back to refresh.

const PAGE_SIZE = 25;
export const canDeleteEffectiveCategory = (categories) => Array.isArray(categories) && categories.length > 0;

const STATUS_META = {
    confident: { bg: '#E8F5E9', fg: '#2E7D32', label: 'Confident' },
    ambiguous: { bg: '#FFF4E5', fg: '#9A5B00', label: 'Ambiguous' },
    manual: { bg: tokens.primaryFaint, fg: tokens.primaryDeep, label: 'Manual' },
    uncategorized: { bg: '#FFF4E5', fg: '#9A5B00', label: 'Not categorized' },
    other: { bg: '#F1F3F4', fg: '#5F6368', label: 'Other' },
};

function StatusPill({ status }) {
    const s = STATUS_META[status] || STATUS_META.other;
    return (
        <span style={{ background: s.bg, color: s.fg, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {s.label}
        </span>
    );
}

const pagerBtn = {
    padding: '3px 10px', border: `1px solid ${tokens.borderMedium}`, borderRadius: 4,
    background: '#FFFFFF', color: tokens.textPrimary, fontSize: 12, fontWeight: 500, cursor: 'pointer',
};

// Hover explanations for the (cryptic) column headers.
export const HEADER_TIPS = {
    response: "The respondent's free-text answer.",
    category: 'The category this response is counted in. Pick a different one to reassign it — a manual choice overrides the automatic classifier and is kept when you re-run it.',
    status: 'How the category was decided — Confident: the classifier placed it firmly · Ambiguous: the classifier was unsure, left unassigned for you to fill · Manual: you assigned it · Other: not a fit for any category.',
    score: "Similarity between the response and its category (0–1). Higher means a closer match.",
    margin: 'Gap between the best category’s similarity and the runner-up. A small margin means the classifier was torn between two categories.',
    decision: 'The classifier’s raw outcome before any manual override: assigned, ambiguous, or other.',
    recipient: 'Who gave this answer. Opens their session in Explore.',
    placement: 'Why this answer is counted here — Assigned by you: your own choice, which overrides the classifier · High-confidence match: the classifier placed it firmly · Possible match: the classifier split it across close categories · Did not fit a category.',
    audio: 'Play the recording captured with this recipient response, when one is available.',
};

// Normalize each raw classification row to an effective category + status,
// resolving the author-label OVERLAY over the classifier output. Exported so
// the toolbar export uses the exact same shape the table shows.
//   status: 'manual' (author override) | 'confident' | 'ambiguous' | 'other'
//           | 'uncategorized' (an answer categorizing has never run over)
//   effIndex: number | 'other' | null (null = ambiguous/unassigned, shows blank)
// The editor lists ANSWERS, not classification rows.  Categorizing runs on
// demand, so an answer recorded since the last run has no classification row of
// its own; driving the table off `rows` made exactly those answers — the ones
// an author most needs to reach — impossible to select or assign.  Every answer
// with text appears, and its classification row is joined on when one exists.
export function buildResponseRows({ rows, labelsBySession, textBySession, recipientIdBySession, audioBySession, categories }) {
    const isValidIndex = (i) => Number.isInteger(i) && i >= 0 && i < (categories || []).length;
    const nameOf = (idx) => {
        const cat = categories[idx];
        return (cat && cat.summary != null && cat.summary !== '') ? cat.summary : `Category ${idx}`;
    };
    const classifiedBySession = new Map((rows || []).filter(row => row && row.session_id != null).map(row => [row.session_id, row]));
    const answered = [];
    const seen = new Set();
    const addSession = (sid) => {
        if (sid == null || seen.has(sid)) return;
        const text = (textBySession?.has?.(sid) && textBySession.get(sid) != null) ? String(textBySession.get(sid)) : '';
        // An answer is present when it has text. Sessions that never answered
        // this question are not answers and stay out of the editor.
        if (text.trim() === '' && !classifiedBySession.has(sid)) return;
        seen.add(sid);
        answered.push(sid);
    };
    // Answer order first, so the table reads in the order responses arrived;
    // any classified session missing from the loaded responses still appears.
    if (textBySession?.forEach) textBySession.forEach((_value, sid) => addSession(sid));
    classifiedBySession.forEach((_row, sid) => addSession(sid));

    return answered.map((sid) => {
        const r = classifiedBySession.get(sid) || {};
        const label = labelsBySession?.get?.(sid);
        // An assignment names its category by identity, so a rename or reorder
        // cannot turn it into a different category.
        const resolved = labelToIndex(categories, label);
        const hasLabel = resolved !== null;
        let effIndex;
        let status;
        if (hasLabel) {
            status = 'manual';
            effIndex = resolved;
        } else if (r.decision === 'assigned') {
            status = 'confident';
            effIndex = Number(r.assigned_category_index);
        } else if (r.decision === 'other') {
            status = 'other';
            effIndex = 'other';
        } else if (!classifiedBySession.has(sid)) {
            // Never categorized, as distinct from categorized-but-ambiguous.
            status = 'uncategorized';
            effIndex = null;
        } else {
            status = 'ambiguous';
            effIndex = null;
        }
        // An ambiguous row is a low-confidence GUESS, not an absence of one.
        // The classifier scored every category and kept its best; discarding it
        // and showing an empty field makes the author start from scratch, which
        // is the whole value of the automatic pass thrown away. Carry the guess
        // through as a proposal the author accepts or overrides.
        let proposedIndex = null;
        if (effIndex == null && status === 'ambiguous') {
            const top = Number(r.top_index);
            if (isValidIndex(top)) {
                proposedIndex = top;
            } else if (r.fractional_mass && typeof r.fractional_mass === 'object') {
                // No stored top index: take the largest share of the split.
                const best = Object.keys(r.fractional_mass)
                    .map(k => [Number(k), Number(r.fractional_mass[k])])
                    .filter(([i, m]) => isValidIndex(i) && Number.isFinite(m))
                    .sort((a, b) => b[1] - a[1])[0];
                if (best) proposedIndex = best[0];
            }
        }
        const catName = effIndex == null ? '' : (effIndex === 'other' ? 'Other' : nameOf(effIndex));
        const text = (textBySession?.has?.(sid) && textBySession.get(sid) != null) ? String(textBySession.get(sid)) : '';
        const recipientId = (recipientIdBySession?.has?.(sid) && recipientIdBySession.get(sid) != null)
            ? recipientIdBySession.get(sid)
            : '';
        const audioPath = audioBySession?.get?.(sid) || '';
        const score = (Array.isArray(r.similarity) && typeof effIndex === 'number' && r.similarity[effIndex] != null)
            ? Number(r.similarity[effIndex])
            : (r.top_score != null ? Number(r.top_score) : null);
        return {
            sid, recipientId, text, effIndex, catName, status, score,
            proposedIndex,
            proposedName: proposedIndex == null ? '' : nameOf(proposedIndex),
            margin: r.margin != null ? Number(r.margin) : null,
            decision: r.decision || '',
            audioPath,
            // Retained for the classified-text CSV/PDF audit export, but not
            // rendered in the author-facing editor table.
            path: r.decision_path || '',
        };
    });
}

export default function CategoryEditPanel({
    deploymentId, question, rows, labelsBySession, textBySession, audioBySession, categories,
    onClose, onLabelsRefresh, onStructureRefresh, onClassificationRefresh, onReclassify,
    reclassifyBusy, reclassifyError, reclassifyOutcome,
}) {
    const qid = question?.id;

    // Writing a category definition clears the question's classification rows
    // server-side: the scores were derived from the previous category text and
    // positions, so they no longer describe this list. Re-read them with the
    // new categories, or the canvas keeps reconciling rows that no longer
    // exist and reports the difference as answers it could not classify.
    const applyCategories = useCallback(async (next) => {
        await putDesignCategories(deploymentId, qid, next);
        await onStructureRefresh?.();
        await onClassificationRefresh?.();
    }, [deploymentId, qid, onStructureRefresh, onClassificationRefresh]);

    // ---- Category management section (add / rename / delete) ----
    const [nameDraft, setNameDraft] = useState({});
    const [renameDirty, setRenameDirty] = useState(false);
    const [renameBusy, setRenameBusy] = useState(false);
    const [renameError, setRenameError] = useState(null);
    const [addName, setAddName] = useState('');

    // Re-seed name drafts whenever the authored categories change (adopt/rename
    // landed) — but not while the author is mid-edit.
    useEffect(() => {
        const d = {};
        categories.forEach((c, i) => { d[i] = c?.summary || ''; });
        setNameDraft(d);
        setRenameDirty(false);
    }, [categories]);

    const categoryName = useCallback((idx) => {
        const cat = categories[idx];
        return (cat && cat.summary != null && cat.summary !== '') ? cat.summary : `Category ${idx}`;
    }, [categories]);

    const saveNames = useCallback(async () => {
        setRenameError(null);
        const next = [];
        const seen = new Set();
        for (let i = 0; i < categories.length; i++) {
            const raw = nameDraft[i] != null ? nameDraft[i] : (categories[i]?.summary || '');
            const name = String(raw).trim();
            if (!name) { setRenameError('Category names cannot be empty.'); return; }
            const k = name.toLowerCase();
            if (seen.has(k)) { setRenameError('Duplicate name: "' + name + '". Names must be unique.'); return; }
            seen.add(k);
            next.push({ ...categories[i], summary: name });
        }
        setRenameBusy(true);
        try {
            await applyCategories(next);
            setRenameDirty(false);
        } catch (e) {
            setRenameError(e?.message || 'Failed to save category names');
        } finally {
            setRenameBusy(false);
        }
    }, [categories, nameDraft, applyCategories]);

    const addCategory = useCallback(async () => {
        const trimmed = String(addName || '').trim();
        if (!trimmed) return;
        if (categories.some((c) => String(c?.summary || '').trim().toLowerCase() === trimmed.toLowerCase())) {
            setRenameError('A category named "' + trimmed + '" already exists.');
            return;
        }
        setRenameError(null);
        setRenameBusy(true);
        try {
            const prefix = categories.map((c) => ({ ...c }));
            await applyCategories([...prefix, { summary: trimmed }]);
            setAddName('');
        } catch (e) {
            setRenameError(e?.message || 'Failed to add category');
        } finally {
            setRenameBusy(false);
        }
    }, [addName, categories, applyCategories]);

    // Delete a category, then re-run the classifier: the server clears the
    // (now index-shifted) classification rows, so a re-run repopulates them.
    const deleteCategory = useCallback(async (index) => {
		if (!canDeleteEffectiveCategory(categories)) {
			setRenameError('There are no analytical categories to delete.');
			return;
		}
        const name = (categories[index] && categories[index].summary) || `Category ${index}`;
        if (typeof window !== 'undefined' && !window.confirm(`Delete "${name}"? Its responses move to Other and the classifier re-runs. Manual assignments to it are cleared.`)) return;
        setRenameError(null);
        setRenameBusy(true);
        try {
            await applyCategories(categories.filter((_, i) => i !== index));
            await onReclassify?.();
        } catch (e) {
            setRenameError(e?.message || 'Failed to delete category');
        } finally {
            setRenameBusy(false);
        }
    }, [categories, applyCategories, onReclassify]);

    // ---- Table state ----
    const [search, setSearch] = useState('');
    const [filterMode, setFilterMode] = useState('all'); // all | uncategorized | ambiguous | overridden | other | cat:<i>
    const [sort, setSort] = useState({ key: 'status', dir: 'asc' });
    const [page, setPage] = useState(0);
    const [pending, setPending] = useState({});
    const [tableError, setTableError] = useState(null);
    const [newCatFor, setNewCatFor] = useState(null);
    const [newCatName, setNewCatName] = useState('');

    // Normalize each classification row to an effective category + status.
    const tableRows = useMemo(
        () => buildResponseRows({ rows, labelsBySession, textBySession, audioBySession, categories }),
        [rows, labelsBySession, textBySession, audioBySession, categories],
    );

    const filtered = useMemo(() => {
        const lower = search.trim().toLowerCase();
        return tableRows.filter((row) => {
            if (lower && !(row.text.toLowerCase().includes(lower) || row.catName.toLowerCase().includes(lower))) return false;
            if (filterMode === 'all') return true;
            if (filterMode === 'ambiguous') return row.status === 'ambiguous';
            if (filterMode === 'uncategorized') return row.status === 'uncategorized';
            if (filterMode === 'overridden') return row.status === 'manual';
            if (filterMode === 'other') return row.effIndex === 'other';
            if (filterMode.startsWith('cat:')) return row.effIndex === Number(filterMode.slice(4));
            return true;
        });
    }, [tableRows, search, filterMode]);

    const sorted = useMemo(() => {
        const dir = sort.dir === 'asc' ? 1 : -1;
        const key = sort.key;
        const arr = filtered.slice();
        arr.sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (typeof av === 'number' && typeof bv === 'number') return av > bv ? dir : (av < bv ? -dir : 0);
            return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
        });
        return arr;
    }, [filtered, sort]);

    const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const pageStart = page * PAGE_SIZE;
    const paged = useMemo(() => sorted.slice(pageStart, pageStart + PAGE_SIZE), [sorted, pageStart]);
    useEffect(() => { setPage((p) => Math.min(p, pageCount - 1)); }, [pageCount]);
    useEffect(() => { setPage(0); }, [search, filterMode]);

    const toggleSort = (key) => setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
    const sortArrow = (key) => (key !== sort.key ? '' : (sort.dir === 'asc' ? ' ▲' : ' ▼'));

    // ---- Writes: LABELS ONLY (override overlay), or category create. ----
    const reassign = useCallback(async (sid, value) => {
        setPending((p) => ({ ...p, [sid]: true }));
        setTableError(null);
        try {
            if (value === '__auto__') {
                await deleteLabel(deploymentId, qid, sid);
            } else {
                await createLabel(deploymentId, qid, { session_id: sid, label: value });
            }
            await onLabelsRefresh?.();
        } catch (e) {
            setTableError(e?.message || 'Failed to reassign response');
        } finally {
            setPending((p) => { const n = { ...p }; delete n[sid]; return n; });
        }
    }, [deploymentId, qid, onLabelsRefresh]);

    // Accept every suggestion currently in view. Reviewing dozens of proposals
    // one dropdown at a time is the same work the automatic pass was meant to
    // remove, so the bulk action follows the active filter and search.
    const acceptAllProposals = useCallback(async (rows) => {
        const proposals = rows.filter(row => row.effIndex == null && row.proposedIndex != null);
        if (!proposals.length) return;
        setTableError(null);
        setPending((p) => ({ ...p, ...Object.fromEntries(proposals.map(row => [row.sid, true])) }));
        let failed = 0;
        for (const row of proposals) {
            try {
                await createLabel(deploymentId, qid, { session_id: row.sid, label: categoryLabelValue(categories, row.proposedIndex) });
            } catch (_) {
                failed += 1;
            }
        }
        setPending((p) => {
            const n = { ...p };
            proposals.forEach(row => { delete n[row.sid]; });
            return n;
        });
        if (failed) setTableError(`${failed} of ${proposals.length} suggestions could not be accepted.`);
        await onLabelsRefresh?.();
    }, [deploymentId, qid, onLabelsRefresh, categories]);

    const createAndAssign = useCallback(async (sid) => {
        const trimmed = String(newCatName || '').trim();
        if (!trimmed) return;
        if (categories.some((c) => String(c?.summary || '').trim().toLowerCase() === trimmed.toLowerCase())) {
            setTableError('A category named "' + trimmed + '" already exists — assign to it instead.');
            return;
        }
        setPending((p) => ({ ...p, [sid]: true }));
        setTableError(null);
        try {
            const prefix = categories.map((c) => ({ ...c }));
            await putDesignCategories(deploymentId, qid, [...prefix, { summary: trimmed }]);
            const newIndex = categories.length; // appended at the end — index is deterministic
            // The label is written before the refreshes so the assignment is
            // already stored when the canvas re-reads; the category write has
            // cleared the classification rows, so those are re-read too.
            await createLabel(deploymentId, qid, { session_id: sid, label: String(newIndex) });
            setNewCatFor(null);
            setNewCatName('');
            await onStructureRefresh?.();
            await onClassificationRefresh?.();
            await onLabelsRefresh?.();
        } catch (e) {
            setTableError(e?.message || 'Failed to create category');
        } finally {
            setPending((p) => { const n = { ...p }; delete n[sid]; return n; });
        }
    }, [newCatName, categories, deploymentId, qid, onStructureRefresh, onClassificationRefresh, onLabelsRefresh]);

    // Escape closes the overlay.
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const filteredLabel = (filterMode !== 'all' || search) ? ' (filtered)' : '';
    // Bulk accept follows the current view, so its count must too.
    const proposalCount = filtered.filter(row => row.effIndex == null && row.proposedIndex != null).length;
    const acceptingAll = Object.keys(pending).length > 1;

    return (
        <>
            <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000 }} />
            <div style={{ position: 'fixed', inset: 0, zIndex: 2001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, pointerEvents: 'none' }}>
                <div style={{ pointerEvents: 'auto', width: 'min(1120px, 96vw)', height: 'min(88vh, 900px)', background: '#FFFFFF', borderRadius: 10, boxShadow: '0 8px 40px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: tokens.fontFamily }}>
                    {/* Header */}
                    <div style={panelStyles.header}>
                        <div>
                            <h2 style={panelStyles.headerTitle}>Edit categories</h2>
                            <div style={panelStyles.headerSubtitle}>{question?.label || question?.name} · {tableRows.length} response{tableRows.length === 1 ? '' : 's'}</div>
                        </div>
                        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: tokens.textSecondary, lineHeight: 1 }}>×</button>
                    </div>

                    {/* Categories: add / rename / delete + re-run automatic */}
                    <div style={{ ...panelStyles.section, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <h3 style={panelStyles.sectionHeading}>Categories</h3>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button onClick={onReclassify} disabled={reclassifyBusy || renameBusy} style={{ ...panelStyles.btnSecondary, ...((reclassifyBusy || renameBusy) ? panelStyles.btnDisabled : {}) }}>
                                    {reclassifyBusy ? 'Re-classifying…' : 'Re-run automatic classification'}
                                </button>
                                <button onClick={saveNames} disabled={renameBusy || !renameDirty} style={{ ...panelStyles.btnPrimary, ...((renameBusy || !renameDirty) ? panelStyles.btnDisabled : {}) }}>
                                    {renameBusy ? 'Saving…' : 'Save names'}
                                </button>
                            </div>
                        </div>
                        {renameError && <div style={{ color: tokens.error, fontSize: 13 }}>{renameError}</div>}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {categories.map((c, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input
                                        type="text"
                                        value={nameDraft[i] != null ? nameDraft[i] : ''}
                                        onChange={(e) => { setNameDraft((d) => ({ ...d, [i]: e.target.value })); setRenameDirty(true); }}
                                        disabled={renameBusy}
                                        style={{ ...panelStyles.textInput, flex: 1, maxWidth: 360 }}
                                    />
                                    <button
                                        onClick={() => deleteCategory(i)}
                                        disabled={renameBusy}
                                        title="Delete this category — its responses move to Other and the classifier re-runs"
                                        style={{ ...panelStyles.actionBtn, color: tokens.error, borderColor: tokens.borderMedium }}
                                    >
                                        Delete
                                    </button>
                                </div>
                            ))}
                            {categories.length === 0 && <span style={{ fontSize: 12, color: tokens.textSecondary }}>No categories yet.</span>}
                            {/* Add a new category */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                                <input
                                    type="text"
                                    value={addName}
                                    onChange={(e) => setAddName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }}
                                    placeholder="New category name…"
                                    disabled={renameBusy}
                                    style={{ ...panelStyles.textInput, flex: 1, maxWidth: 360 }}
                                />
                                <button
                                    onClick={addCategory}
                                    disabled={renameBusy || !addName.trim()}
                                    style={{ ...panelStyles.actionBtn, color: tokens.primaryDeep, borderColor: tokens.primary }}
                                >
                                    + Add
                                </button>
                            </div>
                        </div>
                        <div style={{ fontSize: 11, color: tokens.textSecondary }}>
                            Rename in place (Save names, no re-classification) · Delete moves a category’s responses to Other and
                            re-runs the classifier · Add appends one (Re-run to sort responses into it). Manual reassignments below
                            always override the classifier.
                        </div>
                    </div>

                    {/* Filter / search */}
                    <div style={panelStyles.filterBar}>
                        <input style={panelStyles.searchInput} placeholder="Search responses or categories…" value={search} onChange={(e) => setSearch(e.target.value)} />
                        <select style={panelStyles.select} value={filterMode} onChange={(e) => setFilterMode(e.target.value)}>
                            <option value="all">All responses</option>
                            <option value="uncategorized">Not yet categorized</option>
                            <option value="ambiguous">Ambiguous (unassigned)</option>
                            <option value="overridden">Manually overridden</option>
                            <option value="other">Other</option>
                            {categories.length > 0 && (
                                <optgroup label="By category">
                                    {categories.map((c, i) => <option key={i} value={`cat:${i}`}>{categoryName(i)}</option>)}
                                </optgroup>
                            )}
                        </select>
                        {proposalCount > 0 && (
                            <button
                                onClick={() => acceptAllProposals(filtered)}
                                disabled={acceptingAll}
                                title="Assign every suggested category shown by the current filter"
                                style={{ ...panelStyles.actionBtn, color: tokens.primaryDeep, borderColor: tokens.primary, whiteSpace: 'nowrap' }}
                            >
                                {acceptingAll ? 'Accepting…' : `✓ Accept ${proposalCount} suggestion${proposalCount === 1 ? '' : 's'}`}
                            </button>
                        )}
                    </div>

                    {/* The canvas shows these as well, but this panel is an
                        overlay on top of it — a run started from in here had
                        its result rendered underneath and hidden. */}
                    {reclassifyError && <div role="alert" style={panelStyles.errorBar}>{reclassifyError}</div>}
                    {reclassifyOutcome && !reclassifyError && (
                        <div role="status" style={{ ...panelStyles.errorBar, background: tokens.primaryFaint, color: tokens.primaryDeep, borderColor: tokens.primary }}>
                            {reclassifyOutcome}
                        </div>
                    )}
                    {tableError && <div style={panelStyles.errorBar}>{tableError}</div>}

                    {/* Response table */}
                    <div style={panelStyles.tableContainer}>
                        <table style={panelStyles.table}>
                            <thead>
                                <tr>
                                    <th title={HEADER_TIPS.response} style={{ ...panelStyles.th, ...panelStyles.thSortable, whiteSpace: 'normal', minWidth: 280 }} onClick={() => toggleSort('text')}>Response{sortArrow('text')}</th>
                                    <th title={HEADER_TIPS.category} style={{ ...panelStyles.th, minWidth: 200 }}>Category</th>
                                    <th title={HEADER_TIPS.status} style={{ ...panelStyles.th, ...panelStyles.thSortable }} onClick={() => toggleSort('status')}>Status{sortArrow('status')}</th>
                                    <th title={HEADER_TIPS.score} style={{ ...panelStyles.th, ...panelStyles.thSortable, textAlign: 'right' }} onClick={() => toggleSort('score')}>Score{sortArrow('score')}</th>
                                    <th title={HEADER_TIPS.margin} style={{ ...panelStyles.th, ...panelStyles.thSortable, textAlign: 'right' }} onClick={() => toggleSort('margin')}>Margin{sortArrow('margin')}</th>
                                    <th title={HEADER_TIPS.decision} style={{ ...panelStyles.th, ...panelStyles.thSortable }} onClick={() => toggleSort('decision')}>Decision{sortArrow('decision')}</th>
                                    <th title={HEADER_TIPS.audio} style={panelStyles.th}>Audio</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((row) => {
                                    const busy = !!pending[row.sid];
                                    // A proposal fills the field so the author confirms a guess
                                    // rather than choosing from an empty list. It is not saved
                                    // until accepted, so the row stays ambiguous until then.
                                    const isProposal = row.effIndex == null && row.proposedIndex != null;
                                    const selValue = row.effIndex != null
                                        ? (row.effIndex === OTHER_LABEL ? OTHER_LABEL : categoryLabelValue(categories, row.effIndex))
                                        : (isProposal ? categoryLabelValue(categories, row.proposedIndex) : '');
                                    return (
                                        <tr key={row.sid}>
                                            <td style={{ ...panelStyles.td, whiteSpace: 'normal', maxWidth: 360, color: tokens.textPrimary }}>
                                                {row.text || <span style={{ color: tokens.textTertiary }}>(no text)</span>}
                                            </td>
                                            {/* The cell stacks its control and the accept action. The
                                                shared cell style is nowrap, which would put the button
                                                on the select's line and overlap the next column. */}
                                            <td style={{ ...panelStyles.td, whiteSpace: 'normal' }}>
                                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start', minWidth: 180 }}>
                                                {newCatFor === row.sid ? (
                                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                        <input
                                                            autoFocus
                                                            value={newCatName}
                                                            onChange={(e) => setNewCatName(e.target.value)}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') createAndAssign(row.sid); if (e.key === 'Escape') { setNewCatFor(null); setNewCatName(''); } }}
                                                            placeholder="New category…"
                                                            disabled={busy}
                                                            style={{ ...panelStyles.textInput, minWidth: 120 }}
                                                        />
                                                        <button onClick={() => createAndAssign(row.sid)} disabled={busy || !newCatName.trim()} style={{ ...panelStyles.actionBtn, color: tokens.primaryDeep, borderColor: tokens.primary }}>Add</button>
                                                        <button onClick={() => { setNewCatFor(null); setNewCatName(''); }} disabled={busy} style={panelStyles.actionBtn}>✕</button>
                                                    </div>
                                                ) : (
                                                    <select
                                                        disabled={busy}
                                                        value={selValue}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            if (v === '__new__') { setNewCatFor(row.sid); setNewCatName(''); }
                                                            else if (v === '__auto__') { reassign(row.sid, '__auto__'); }
                                                            else if (v !== '') { reassign(row.sid, v); }
                                                        }}
                                                        style={{ ...panelStyles.select, width: '100%', minWidth: 180, ...(row.effIndex == null ? { color: isProposal ? tokens.textPrimary : tokens.textTertiary, borderColor: tokens.warning } : {}) }}
                                                    >
                                                        {row.effIndex == null && !isProposal && <option value="" disabled>Choose category…</option>}
                                                        {categories.map((c, i) => <option key={i} value={categoryLabelValue(categories, i)}>{categoryName(i)}</option>)}
                                                        <option value={OTHER_LABEL}>Other</option>
                                                        <option value="__new__">➕ New category…</option>
                                                        {row.status === 'manual' && <option value="__auto__">↺ Revert to automatic</option>}
                                                    </select>
                                                )}
                                                {isProposal && newCatFor !== row.sid && (
                                                    <button
                                                        onClick={() => reassign(row.sid, categoryLabelValue(categories, row.proposedIndex))}
                                                        disabled={busy}
                                                        title={`Accept the suggested category "${row.proposedName}"`}
                                                        style={{ ...panelStyles.actionBtn, alignSelf: 'flex-start', whiteSpace: 'nowrap', color: tokens.primaryDeep, borderColor: tokens.primary }}
                                                    >
                                                        {busy ? 'Accepting…' : '✓ Accept suggestion'}
                                                    </button>
                                                )}
                                              </div>
                                            </td>
                                            <td style={panelStyles.td}><StatusPill status={row.status} /></td>
                                            <td style={{ ...panelStyles.td, textAlign: 'right' }}>{row.score != null ? row.score.toFixed(2) : '—'}</td>
                                            <td style={{ ...panelStyles.td, textAlign: 'right' }}>{row.margin != null ? row.margin.toFixed(2) : '—'}</td>
                                            <td style={panelStyles.td}>{row.decision || '—'}</td>
                                            <td style={panelStyles.td}>{row.audioPath ? <AudioPlayChip gcsPath={row.audioPath} /> : '—'}</td>
                                        </tr>
                                    );
                                })}
                                {paged.length === 0 && (
                                    <tr><td colSpan={7} style={panelStyles.emptyState}>No responses match this filter.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer: counts + pager + done */}
                    <div style={panelStyles.footer}>
                        <span>{filtered.length} of {tableRows.length} response{tableRows.length === 1 ? '' : 's'}{filteredLabel}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {sorted.length > PAGE_SIZE && (
                                <>
                                    <span>{pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, sorted.length)} of {sorted.length}</span>
                                    <button style={pagerBtn} disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>‹ Prev</button>
                                    <button style={pagerBtn} disabled={page >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}>Next ›</button>
                                </>
                            )}
                            <button onClick={onClose} style={panelStyles.btnPrimary}>Done</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
